<script setup>
import FormFieldValidation from '@convenia/components/CFormBuilder/fragments/FormFieldValidation.vue'
import { ref, computed } from 'vue'
import defaultProps from './props'

const MB_FACTOR = 1000000

const input = ref(null)
const errorMessage = ref(null)
const reader = new FileReader()

const props = defineProps(defaultProps)
const emit = defineEmits([ 'input' ])

const limitInBytes = computed(() => props.sizeLimit * MB_FACTOR)

const styles = computed(() => ({
  '--width': `${props.width}px`,
  '--height': `${props.height}px`,
}))

const getFileExtension = (file) => {
  const index = file.name?.lastIndexOf('.')

  if (index < 1) return ''

  return file.name.substr(index + 1).toLowerCase()
}

const getErrorMessage = (file) => {
  if (!file) return ''

  const extension = getFileExtension(file)

  if (!props.extensions.includes(extension))
    return 'Formato de arquivo não suportado.'

  if (file.size > limitInBytes.value)
    return `Tamanho limite de ${props.sizeLimit} MB excedido.`

  return ''
}

const onAddFile = ({ target }) => {
  const [ file ] = target.files

  if (!file)
    return

  errorMessage.value = getErrorMessage(file)

  if (errorMessage.value)
    return emit('input', null)

  reader.readAsDataURL(file)
  reader.onload = () =>
    emit('input', {
      name: file.name,
      size: file.size,
      src: reader.result,
      data: file,
    })
}

const onSelectFile = () => input.value?.click()

const onRemoveFile = () => emit('input', null)
</script>

<template>
  <div class="c-upload-image" :style="styles">
    <div class="wrapper">
      <c-add
        v-if="!value"
        :text="buttonText"
        data-testid="image-file-add"
        @add="onSelectFile"
      >
        <c-typo
          v-if="placeholder"
          class="placeholder"
          type="body-1"
          opacity="base-30"
        >
          {{ placeholder }}
        </c-typo>

        <form-field-validation
          v-if="errorMessage"
          class="validation"
          :message="errorMessage"
        />
      </c-add>

      <template v-else>
        <img
          class="image"
          :src="value.src"
        />

        <div class="actions">
          <c-button
            shadow
            flat
            flat-border
            class="edit"
            icon="pen-edit-17"
            icon-size="24"
            @click.prevent="onSelectFile"
          />

          <c-button
            shadow
            flat
            error
            flat-border
            class="remove"
            icon="trash"
            icon-size="24"
            @click.prevent="onRemoveFile"
          />
        </div>
      </template>

    </div>

    <input
      ref="input"
      class="input"
      type="file"
      name="file-name"
      id="test"
      :accept="extensions.map(ext => '.' + ext)"
      @change="onAddFile"
    />
  </div>
</template>

<style lang="scss">
.c-upload-image {
  margin-bottom: 10px;

  & > .wrapper {
    height: var(--height);
    max-width: var(--width);
    background-color: color-var(text, base-02);
    border-radius: 5px;
    position: relative;

    & > .c-add {
      padding: 20px;
      height: 100%;

      & > .c-typo {
        max-width: 420px;
        text-align: center;
      }
    }

    & > .placeholder {
      word-wrap: wrap;
    }

    & > .image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      border-radius: 5px;
    }

    & > .actions {
      display: flex;
      align-items: center;
      position: absolute;
      bottom: 20px;
      right: 20px;
      gap: 10px;

      & > .c-button {
        background: white;
      }
    }
  }

  & > .input { display: none; }
}
</style>

<script>
export default { name: 'CUploadImage' }
</script>
